/**
 * Google Analytics 4 (gtag.js) for the marketing site.
 *
 * GA4 sets _ga / _ga_<container> cookies, so — like Application Insights — it
 * loads ONLY after the visitor accepts analytics (CookieBanner) or on a return
 * visit whose stored choice is "accepted" (main.tsx). Advertising storage is
 * declined via Consent Mode: this site does no ad measurement.
 */
export const GA_MEASUREMENT_ID: string =
  (import.meta.env.VITE_GA_MEASUREMENT_ID as string | undefined) || 'G-NY8C31FQJ1';

const SCRIPT_ID = 'p365-gtag';

type Gtag = (...args: unknown[]) => void;

declare global {
  interface Window {
    dataLayer?: unknown[];
    gtag?: Gtag;
  }
}

function disableFlag(): string {
  return `ga-disable-${GA_MEASUREMENT_ID}`;
}

/** Load gtag.js and configure the property. Idempotent. */
export function initGoogleAnalytics(): void {
  if (typeof window === 'undefined' || typeof document === 'undefined') return;
  if (!GA_MEASUREMENT_ID) return;
  (window as unknown as Record<string, unknown>)[disableFlag()] = false;
  if (document.getElementById(SCRIPT_ID)) return;

  const dataLayer: unknown[] = window.dataLayer || [];
  window.dataLayer = dataLayer;
  const gtag: Gtag = function gtag() {
    // gtag.js reads `arguments`, not a spread array.
    // eslint-disable-next-line prefer-rest-params
    dataLayer.push(arguments);
  };
  window.gtag = gtag;
  gtag('consent', 'default', {
    ad_storage: 'denied',
    ad_user_data: 'denied',
    ad_personalization: 'denied',
    analytics_storage: 'granted',
  });
  gtag('js', new Date());
  gtag('config', GA_MEASUREMENT_ID, { anonymize_ip: true });

  const script = document.createElement('script');
  script.id = SCRIPT_ID;
  script.async = true;
  script.src = `https://www.googletagmanager.com/gtag/js?id=${encodeURIComponent(GA_MEASUREMENT_ID)}`;
  document.head.appendChild(script);
}

/** Stop collection immediately; identifiers are removed by cookieConsent. */
export function disableGoogleAnalytics(): void {
  if (typeof window === 'undefined') return;
  (window as unknown as Record<string, unknown>)[disableFlag()] = true;
  window.gtag?.('consent', 'update', { analytics_storage: 'denied' });
}

export function isGoogleAnalyticsLoaded(): boolean {
  return typeof document !== 'undefined' && !!document.getElementById(SCRIPT_ID);
}
